import {
  ClipboardList,
  CreditCard,
  FileText,
  LayoutGrid,
  ListChecks,
  MessageSquareWarning,
  Salad,
  Settings,
  Star,
  Store,
  Table2,
  Tag,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";

/** Every portal path in one place (links, redirects and the router all use these). */
export const ROUTES = {
  signIn: "/sign-in",
  overview: "/",
  brands: "/brands",
  plans: "/plans",
  users: "/users",
  subscriptions: "/subscriptions",
  disputes: "/disputes",
  reviews: "/reviews",
  payments: "/payments",
  payouts: "/payouts",
  ledger: "/ledger",
  reconciliation: "/reconciliation",
  promoCodes: "/promo-codes",
  dispatchTemplates: "/dispatch-templates",
  settings: "/settings",
  auditLog: "/audit-log",
} as const;

/** Paths with an id in them (the router uses the `:param` patterns, links use the functions). */
export const ROUTE_PATTERNS = {
  brand: "/brands/:brandId",
  user: "/users/:userId",
  newFreePlan: "/subscriptions/new",
  subscription: "/subscriptions/:subscriptionId",
  payment: "/payments/:paymentId",
  payout: "/payouts/:payoutId",
} as const;

export const routeTo = {
  brand: (brandId: string) => `/brands/${encodeURIComponent(brandId)}`,
  user: (userId: string) => `/users/${encodeURIComponent(userId)}`,
  subscription: (subscriptionId: string) => `/subscriptions/${encodeURIComponent(subscriptionId)}`,
  /** The free-plan form, optionally for a customer already chosen (from their page). */
  newFreePlan: (userId?: string) =>
    `/subscriptions/new${userId ? `?userId=${encodeURIComponent(userId)}` : ""}`,
  /** The subscription list filtered to one brand or one customer. */
  subscriptionsOf: (filter: { brandId?: string; userId?: string }) =>
    `/subscriptions?${new URLSearchParams(filter as Record<string, string>).toString()}`,
  /** The disputes of one brand (open ones first, as on the list). */
  disputesOf: (brandId: string) => `/disputes?brandId=${encodeURIComponent(brandId)}`,
  payment: (paymentId: string) => `/payments/${encodeURIComponent(paymentId)}`,
  /** Payments of one customer, brand or plan. */
  paymentsOf: (filter: { userId?: string; brandId?: string; subscriptionId?: string }) =>
    `/payments?${new URLSearchParams(filter as Record<string, string>).toString()}`,
  payout: (payoutId: string) => `/payouts/${encodeURIComponent(payoutId)}`,
  /** The payout history of one brand. */
  payoutsOf: (brandId: string) => `/payouts?tab=history&brandId=${encodeURIComponent(brandId)}`,
  /** Ledger entries of one brand, payment, plan or payout, or of an account or type over some days. */
  ledgerOf: (filter: {
    brandId?: string;
    paymentId?: string;
    subscriptionId?: string;
    payoutId?: string;
    account?: string;
    type?: string;
    from?: string;
    to?: string;
  }) => `/ledger?${new URLSearchParams(filter as Record<string, string>).toString()}`,
  /** The audit trail of one record, or of one person's changes. */
  auditOf: (filter: { entityType?: string; entityId?: string; actorUserId?: string }) =>
    `/audit-log?${new URLSearchParams(filter as Record<string, string>).toString()}`,
  /** The reviews of one brand. */
  reviewsOf: (brandId: string) => `/reviews?brandId=${encodeURIComponent(brandId)}`,
};

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
}

export const NAV: { section?: string; items: NavItem[] }[] = [
  {
    items: [
      { label: "Overview", to: ROUTES.overview, icon: LayoutGrid },
      { label: "Brands", to: ROUTES.brands, icon: Store },
      { label: "Plans", to: ROUTES.plans, icon: Salad },
      { label: "Users", to: ROUTES.users, icon: Users },
      { label: "Subscriptions", to: ROUTES.subscriptions, icon: ClipboardList },
      { label: "Disputes", to: ROUTES.disputes, icon: MessageSquareWarning },
      { label: "Reviews", to: ROUTES.reviews, icon: Star },
    ],
  },
  {
    section: "Money",
    items: [
      { label: "Payments and refunds", to: ROUTES.payments, icon: CreditCard },
      { label: "Payouts", to: ROUTES.payouts, icon: Wallet },
      { label: "Ledger", to: ROUTES.ledger, icon: Table2 },
      { label: "Reconciliation", to: ROUTES.reconciliation, icon: ListChecks },
    ],
  },
  {
    section: "Setup",
    items: [
      { label: "Promo codes", to: ROUTES.promoCodes, icon: Tag },
      { label: "Dispatch messages", to: ROUTES.dispatchTemplates, icon: FileText },
      { label: "Settings", to: ROUTES.settings, icon: Settings },
      { label: "Audit log", to: ROUTES.auditLog, icon: FileText },
    ],
  },
];
